import { useState } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button, Chip, Stack,
  alpha, useTheme, IconButton, Paper, Divider, Switch,
  Snackbar, Alert, Slider, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useApp } from '../contexts';
import { parseHQB } from '../utils';

export default function Settings() {
  const theme = useTheme();
  const { state, updateSettings, removeBank, addBank } = useApp();
  const [activeTab, setActiveTab] = useState<'quiz' | 'banks' | 'about'>('quiz');
  const [uploadMsg, setUploadMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.hqb')) {
      setUploadMsg({ type: 'error', text: '仅支持 .hqb 格式题库文件' });
      return;
    }
    try {
      const text = await file.text();
      const hqb = parseHQB(text);
      if (!hqb) { setUploadMsg({ type: 'error', text: '文件格式无效' }); return; }
      addBank(hqb, 'user');
      setUploadMsg({ type: 'success', text: `已导入：${hqb.metadata.title}` });
      e.target.value = '';
    } catch { setUploadMsg({ type: 'error', text: '文件读取失败' }); }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>设置</Typography>

      <Stack direction="row" sx={{ mb: 3, gap: 0.8 }}>
        {(['quiz', 'banks', 'about'] as const).map(key => {
          const labels = { quiz: '刷题设置', banks: '题库管理', about: '关于' };
          return (
            <Chip key={key} label={labels[key]} onClick={() => setActiveTab(key)}
              color={activeTab === key ? 'primary' : 'default'}
              variant={activeTab === key ? 'filled' : 'outlined'}
              sx={{ fontWeight: activeTab === key ? 600 : 400 }} />
          );
        })}
      </Stack>

      {/* Quiz Settings */}
      {activeTab === 'quiz' && (
        <Card elevation={0} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 4 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Quick count */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>默认快速开始数量</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ alignItems: 'center', gap: 2 }}>
                <Slider
                  value={state.settings.defaultQuickCount}
                  onChange={(_, v) => updateSettings({ defaultQuickCount: v as number })}
                  min={1} max={200} step={1} valueLabelDisplay="auto" sx={{ flex: 1 }}
                />
                <TextField
                  type="number" size="small"
                  value={state.settings.defaultQuickCount}
                  onChange={e => {
                    const v = parseInt(e.target.value);
                    if (!isNaN(v)) updateSettings({ defaultQuickCount: Math.max(1, v) });
                  }}
                  slotProps={{ htmlInput: { min: 1, style: { width: 60, textAlign: 'center' } } }}
                  sx={{ width: 80 }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Auto next */}
            <SettingToggle label="答对后自动下一题" checked={state.settings.autoNext} onChange={v => updateSettings({ autoNext: v })} />
            <SettingRow label="自动跳转延时">
              <TextField type="number" size="small" value={state.settings.autoNextDelay}
                onChange={e => updateSettings({ autoNextDelay: Math.max(100, Number(e.target.value) || 800) })}
                slotProps={{ htmlInput: { min: 100, step: 100 } }} sx={{ width: 120 }} />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>ms</Typography>
            </SettingRow>

            <Divider />

            {/* Question / Option shuffle */}
            <SettingToggle label="题目乱序" checked={state.settings.shuffleQuestions} onChange={v => updateSettings({ shuffleQuestions: v })} />
            <SettingToggle label="选项乱序" checked={state.settings.shuffleOptions} onChange={v => updateSettings({ shuffleOptions: v })} />

            <Divider />

            {/* Random bank */}
            <SettingToggle
              label="随机题库抽题"
              checked={state.settings.randomBank}
              onChange={v => updateSettings({ randomBank: v })}
            />

            <Divider />

            {/* Exam mode */}
            <Box>
              <SettingToggle
                label="考试模式（全程无反馈，交卷后统一评估）"
                checked={state.settings.examMode}
                onChange={v => updateSettings({
                  examMode: v,
                  homeworkMode: v ? false : state.settings.homeworkMode,
                  halfExamMode: v ? false : state.settings.halfExamMode,
                })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                作答过程不显示正确/错误提示，交卷后一次性展示结果
              </Typography>
            </Box>

            <Divider />

            {/* Homework mode */}
            <Box>
              <SettingToggle
                label="作业模式（实时判题，答错高亮正确选项）"
                checked={state.settings.homeworkMode}
                onChange={v => updateSettings({ homeworkMode: v, halfExamMode: v ? false : state.settings.halfExamMode, examMode: v ? false : state.settings.examMode })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                答错时正确选项以绿色高亮显示
              </Typography>
            </Box>

            <Divider />

            {/* Half-exam mode */}
            <Box>
              <SettingToggle
                label="半考试模式（答错可查看答案后继续）"
                checked={state.settings.halfExamMode}
                onChange={v => updateSettings({ halfExamMode: v, homeworkMode: v ? false : state.settings.homeworkMode, examMode: v ? false : state.settings.examMode })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                答错后锁定选项，可查看正确答案或重试到对后继续
              </Typography>
            </Box>

            <Divider />

            {/* Show explanation */}
            <Box>
              <SettingToggle label="交卷后显示解析" checked={state.settings.showExplanation} onChange={v => updateSettings({ showExplanation: v })} />
            </Box>

            <Divider />

            {/* Nav on quiz */}
            <Box>
              <SettingToggle
                label="刷题页显示导航栏"
                checked={state.settings.showNavOnQuiz}
                onChange={v => updateSettings({ showNavOnQuiz: v })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                开启后，刷题页面底部将显示全局导航栏
              </Typography>
            </Box>

            <Divider />

            {/* Re-quiz auto mode */}
            <Box>
              <SettingToggle
                label="重刷时自动延续原有刷题模式"
                checked={state.settings.reQuizAutoMode}
                onChange={v => updateSettings({ reQuizAutoMode: v })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                开启后重刷将直接沿用当前会话的刷题模式；关闭后需手动选择
              </Typography>
            </Box>

          </CardContent>
        </Card>
      )}

      {/* Bank Management */}
      {activeTab === 'banks' && (
        <Card elevation={0} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 4 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed', bgcolor: 'transparent' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>上传题库文件</Typography>
              <Button variant="contained" component="label" size="small" sx={{ borderRadius: 3 }}>
                选择 .hqb 文件
                <input type="file" accept=".hqb" hidden onChange={handleFileUpload} />
              </Button>
            </Paper>

            {state.banks.length === 0 ? (
              <Typography color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>暂无题库</Typography>
            ) : (
              state.banks.map(bank => (
                <Paper key={bank.id} variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: 'transparent' }}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Chip size="small" label={bank.source === 'builtin' ? '内置' : '用户'}
                          color={bank.source === 'builtin' ? 'secondary' : 'primary'} variant="outlined"
                          sx={{ fontWeight: 600, height: 22 }} />
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>{bank.name}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {bank.hqb.questions.length} 题 · {bank.hqb.groups.length} 组
                      </Typography>
                    </Box>
                    {bank.source === 'user' && (
                      <IconButton size="small" onClick={() => setDeleteConfirmId(bank.id)}>
                        <DeleteOutlineRoundedIcon fontSize="small" color="error" />
                      </IconButton>
                    )}
                  </Stack>
                </Paper>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* About */}
      {activeTab === 'about' && (
        <Card elevation={0} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 4 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box
                sx={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>H</Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>HodQuiz Helper</Typography>
              <Typography variant="body2" color="text.secondary">智能刷题助手</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <InfoRow label="开发者" value="云云" />
              <InfoRow label="英文名" value="DirRain" />
              <InfoRow label="工作室" value="禾云工作室" />
              <InfoRow label="英文名" value="HoCloud Technology Studio" />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>技术栈</Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.8 }}>
              {['React 19', 'TypeScript', 'MUI v9', 'Vite', 'React Router'].map(t => (
                <Chip key={t} label={t} size="small" variant="outlined" />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Snackbar open={!!uploadMsg} autoHideDuration={3000} onClose={() => setUploadMsg(null)}>
        {uploadMsg ? (
          <Alert severity={uploadMsg.type} onClose={() => setUploadMsg(null)} variant="filled" sx={{ borderRadius: 3 }}>
            {uploadMsg.text}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} slotProps={{ paper: { sx: { borderRadius: 4, maxWidth: 360 } } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>确认移除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            移除题库后将无法恢复，确定要移除该题库及其所有题目吗？
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteConfirmId(null)} sx={{ borderRadius: 3 }}>取消</Button>
          <Button variant="contained" color="error" onClick={() => { if (deleteConfirmId) { removeBank(deleteConfirmId); setDeleteConfirmId(null); } }} sx={{ borderRadius: 3 }}>
            确认移除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 130 }}>{label}</Typography>
      {children}
    </Stack>
  );
}

function SettingToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
      <Switch checked={checked} onChange={(_, v) => onChange(v)} />
    </Stack>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
    </Stack>
  );
}
